import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Preamble, PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { useContentQuery } from '../ContentGrid/ContentQueryContext';
import { CardFields, ContentOverride } from '../CardListingShared';
import { CtaFields, HeadlineFields } from 'lib/types/headlineFields';
import { Themes } from 'lib/themes';
import { ContentGridCardAlignment } from '../ContentGrid/ContentGrid';
import { useEffect, useRef, useState } from 'react';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { Splide } from '@splidejs/react-splide';
import { CarouselMain } from './CarouselMain';
import carouselStyles from './Carousel.module.scss';
import clsx from 'clsx';

export interface CarouselProps extends HeadlineFields, CtaFields {
  theme?: Themes;
  contentOverrides: Record<string, ContentOverride>;
  fallbackImageUrl?: string;
  fieldsToShow: CardFields[];
  gridAspectRatio?: ImageAspectRatio;
  preambleOrientation?: PreambleOrientation;
  cardAlignment?: ContentGridCardAlignment;
  showPagination?: boolean;
}

const MAX_CARDS = 8;

export const Carousel = (props: CarouselProps) => {
  const splideRef = useRef<Splide>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [currentPerPage, setCurrentPerPage] = useState(3);
  const { searchResponse } = useContentQuery();
  const preambleAlignment = props.preambleOrientation as PreambleOrientation;

  // Limit cards to maximum 8
  const cards = searchResponse?.Results?.slice(0, MAX_CARDS) || [];
  const totalCards = cards.length;

  useEffect(() => {
    if (splideRef.current?.splide) {
      const splide = splideRef.current.splide;

      const updateStates = () => {
        setActiveIndex(splide.index);
        setIsAtStart(splide.index === 0);
        setIsAtEnd(splide.index >= totalCards - (splide.options.perPage || 3));
        // Get the current perPage from Splide's options
        const currentPerPage = splide.options.perPage || 3;
        setCurrentPerPage(currentPerPage);
      };

      splide.on('move', updateStates);
      splide.on('resize', updateStates);

      // Initialize states
      updateStates();
    }
  }, [totalCards]);

  const handlePrev = () => {
    splideRef.current?.splide?.go('<');
  };

  const handleNext = () => {
    splideRef.current?.splide?.go('>');
  };

  // Calculate total pages based on current perPage (updates dynamically with breakpoints)
  const totalPages = Math.max(1, totalCards - currentPerPage + 1);

  return (
    <BrandAndThemeProvider
      theme={props.theme}
      className={clsx(carouselStyles.carouselWrapper)}
      data-component="authorable/shared/card-listing/carousel/carousel"
    >
      <Preamble
        headlineEyebrow={props.headlineEyebrow}
        headlineIcon={props.headlineIcon}
        headline={props.headline}
        headlineSubhead={props.headlineSubhead}
        headlineDescription={props.headlineDescription}
        cta1={props.cta1}
        cta2={props.cta2}
        align={preambleAlignment}
      >
        <CarouselMain
          headline={props.headline}
          splideRef={splideRef}
          cards={cards}
          totalPages={totalPages}
          activeIndex={activeIndex}
          isAtStart={isAtStart}
          isAtEnd={isAtEnd}
          preambleAlignment={preambleAlignment}
          cardAlignment={props.cardAlignment}
          contentOverrides={props.contentOverrides}
          fallbackImageUrl={props.fallbackImageUrl}
          fieldsToShow={props.fieldsToShow}
          gridAspectRatio={props.gridAspectRatio}
          handlePrev={handlePrev}
          handleNext={handleNext}
        />
      </Preamble>
    </BrandAndThemeProvider>
  );
};
